// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useMemo } from 'react';
import styled from '@emotion/styled';

import Image from 'presentations/views/components/atoms/Image';
import Button from 'presentations/views/components/atoms/Button';
import EventTypeSubmitField from 'presentations/views/components/molecules/EventTypeSubmitField';
import {
  spacing,
  mediaMax,
  fontWeight,
  fontSize,
  isMediaMax,
} from 'presentations/views/styles';
import { Trail } from 'presentations/views/components/atoms/Transition';
import { black, gray01, white } from 'presentations/views/styles/color';
import { EventTypeServiceListItem } from 'presentations/api/schema/event_type_service';
import Icon from 'presentations/views/components/atoms/Icon';
import { formatTaxExcludedPrice } from 'presentations/views/helpers/formatter';
import FormLabel from 'presentations/views/components/molecules/FormLabel';

export type Props = {
  color: string;
  isLightColor: boolean;
  isLoading?: boolean;
  isDisabled: boolean;
  services: EventTypeServiceListItem[];
  selectedServices: EventTypeServiceListItem[];
  onSelectService?: any;
  onSubmit?: any;
};

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isMobile = isMediaMax('tablet');

  const optionalServices = useMemo(
    () =>
      props.services
        .filter((service) => !service.isRequired)
        .sort((a, b) => a.sort - b.sort),
    [props.services],
  );

  return (
    <Form>
      <Scroller>
        <Trail isOpen={true}>
          {optionalServices.length > 0 && (
            <>
              <FormLabel isNotRequired>{t('option service')}</FormLabel>
              {optionalServices.map((service, index) => {
                return (
                  <Block key={index}>
                    <ServiceButton
                      isLoading={props.isLoading}
                      isDisableRipple={true}
                      variant="outlined"
                      onClick={(e: SyntheticEvent) =>
                        props.onSelectService(service)
                      }
                      size="large"
                      borderColor={
                        props.selectedServices.some((selected) => {
                          return selected.uid === service.uid;
                        })
                          ? props.color
                          : '#e4e4e4'
                      }
                    >
                      <CheckIcon fontSize="small" name="check-solid" />
                      <ServiceRightContainer>
                        <ServiceRightUpperContainer>
                          {service.coverImage && (
                            <ServiceCoverImage
                              src={service.coverImage}
                              alt={service.name!}
                              width={isMobile ? 120 : 64}
                              height={isMobile ? 120 : 64}
                              fit="crop"
                            />
                          )}
                          <ServiceNameContainer>
                            <ServiceName>{service.name}</ServiceName>
                            <ServicePrice>
                              {Boolean(service.isShowPrice) &&
                                formatTaxExcludedPrice(service)}
                            </ServicePrice>
                          </ServiceNameContainer>
                        </ServiceRightUpperContainer>
                        <ServiceDescription>
                          {service.description}
                        </ServiceDescription>
                      </ServiceRightContainer>
                    </ServiceButton>
                  </Block>
                );
              })}
            </>
          )}
        </Trail>
      </Scroller>
      <Submit>
        <EventTypeSubmitField
          isLoading={props.isLoading}
          submitLabel={t('submit')}
          onSubmit={props.onSubmit}
          isDisabled={props.isDisabled}
          color={props.color}
          isLightColor={props.isLightColor}
        />
      </Submit>
    </Form>
  );
}

const Block = styled.div`
  margin-bottom: ${spacing(12)};
  :last-of-type {
    /* margin-bottom: 0; */
  }
`;
const Submit = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 70px;
  padding-top: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  background-color: ${white};
  ${mediaMax.smartphone} {
    position: inherit;
  }
`;

const Scroller = styled.div`
  height: 370px;
  overflow: auto;
  ${mediaMax.smartphone} {
    height: auto;
    overflow: inherit;
  }
`;

const SectionTitle = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
    margin-bottom: ${spacing(6)};
  }
`;

const Form = styled.div`
  display: block;
  height: 460px;
  width: 100%;
  position: relative;
  padding: ${spacing(8)} ${spacing(40)} 0;
  ${mediaMax.smartphone} {
    height: inherit;
    padding: ${spacing(8)} ${spacing(16)} 0;
  }
`;

const CheckIcon = styled(Icon)`
  min-width: ${spacing(32)};
  margin-right: ${spacing(32)};
`;
const ServiceRightContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ServiceNameContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ServiceRightUpperContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const ServiceButton = styled(Button)<{ borderColor: string }>`
  width: 100%;
  border-color: ${({ borderColor }) => borderColor};
  color: ${({ borderColor }) => borderColor};
  font-weight: ${fontWeight('normal')};
  justify-content: start;
  padding: ${spacing(16)};
`;

const ServiceCoverImage = styled(Image)`
  margin-right: ${spacing(8)};
  width: 60px;
  height: 60px;
  object-fit: cover;
  ${mediaMax.tablet} {
    width: 64px;
    height: 64px;
  }
`;
const ServiceName = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-top: ${spacing(8)};
  margin-bottom: ${spacing(4)};
  text-align: left;

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
    margin-bottom: ${spacing(3)};
  }
`;
const ServicePrice = styled.div`
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  text-align: left;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
const ServiceDescription = styled.div`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(13)};
  color: ${gray01};
  text-align: left;

  ${mediaMax.tablet} {
    margin-top: ${spacing(4)};
    font-size: ${fontSize(11)};
  }
`;
